import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
    ArrowLeft, Home, MapPin, Users, Clock, Phone, Mail,
    Building2, TreePine, Coffee, Heart, Sparkles, ChevronRight,
    Check, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for dynamic icons
const iconMap = {
    Building2, TreePine, Coffee, Heart, Home, MapPin, Users, Clock, Sparkles
};

export function WelcomePage() {
    const navigate = useNavigate();
    const { organization } = useOrganization();
    const { token } = useAuth();
    const [activeSection, setActiveSection] = useState('intro');
    const [siteAreas, setSiteAreas] = useState([]);
    const [keyContacts, setKeyContacts] = useState([]);
    const [openingHours, setOpeningHours] = useState([]);
    const [loading, setLoading] = useState(true);

    const sections = [
        { id: 'intro', title: 'Introduction', icon: Home },
        { id: 'site', title: 'Site Overview', icon: MapPin },
        { id: 'contacts', title: 'Key Contacts', icon: Users },
        { id: 'hours', title: 'Opening Hours', icon: Clock },
        { id: 'first-week', title: 'Your First Week', icon: Star },
    ];

    useEffect(() => {
        if (token) {
            fetchWelcomeData();
        } else {
            // Set defaults for non-authenticated users
            setDefaults();
            setLoading(false);
        }
    }, [token]);

    // Check if this is Burston (commercial) or Elysian (residential)
    const isBurston = organization?.slug === 'burston' || organization?.name?.toLowerCase().includes('burston');

    const setDefaults = () => {
        if (isBurston) {
            // Burston Garden Centre defaults
            setSiteAreas([
                { id: '1', title: 'Garden Centre', description: 'Explore our extensive range of plants, garden furniture, and outdoor living essentials.', icon: 'TreePine' },
                { id: '2', title: 'The Restaurant', description: 'Delicious home-cooked meals, breakfast, and our famous afternoon tea.', icon: 'Coffee' },
                { id: '3', title: 'Gift & Home', description: 'Unique gifts, homeware, and seasonal decorations for every occasion.', icon: 'Sparkles' },
                { id: '4', title: 'Outdoor Living', description: 'BBQs, garden furniture, and everything for outdoor entertaining.', icon: 'Home' },
            ]);
            setKeyContacts([
                { id: '1', role: 'Main Reception', description: 'General enquiries and information about the garden centre.', phone: '01727 832444', availability: 'Daily, 9am-5:30pm' },
                { id: '2', role: 'Restaurant Bookings', description: 'For afternoon tea and group dining reservations.', phone: '01727 832444', availability: 'Daily, 9am-4pm' },
                { id: '3', role: 'Plant Advice', description: 'Expert gardening advice from our knowledgeable team.', phone: '01727 832444', availability: 'Daily, 9am-5pm' },
                { id: '4', role: 'Events & Enquiries', description: 'Information about upcoming events and activities.', phone: '01727 832444', availability: 'Mon-Sat, 9am-5pm' },
            ]);
            setOpeningHours([
                { id: '1', venue: 'Garden Centre', weekday_hours: '9:00am - 5:30pm', weekend_hours: 'Sat: 9am-5:30pm, Sun: 10:30am-4:30pm' },
                { id: '2', venue: 'Restaurant', weekday_hours: '9:00am - 4:30pm', weekend_hours: '9:00am - 4:30pm' },
                { id: '3', venue: 'Breakfast', weekday_hours: 'Until 11:30am', weekend_hours: 'Until 11:30am' },
                { id: '4', venue: 'Hot Meals', weekday_hours: '11:30am - 3:00pm', weekend_hours: '11:30am - 3:00pm' },
            ]);
        } else {
            // Elysian Residences defaults
            setSiteAreas([
                { id: '1', title: 'Apartments & Cottages', description: 'Architecturally designed homes with garden views and modern amenities.', icon: 'Building2' },
                { id: '2', title: 'The Clubhouse', description: 'Restaurant, bar, lounges, and private dining facilities.', icon: 'Coffee' },
                { id: '3', title: 'Gardens & Grounds', description: 'Landscaped gardens, woodland walks, and scenic pond.', icon: 'TreePine' },
                { id: '4', title: 'Wellness Centre', description: 'Fitness studio, spa treatment rooms, and swimming pool.', icon: 'Heart' },
            ]);
            setKeyContacts([
                { id: '1', role: 'Reception / Concierge', description: 'Your first point of contact for general enquiries.', phone: '07957 743331', availability: '24 hours' },
                { id: '2', role: 'Maintenance Team', description: 'For repairs, technical issues, or home improvements.', phone: '07957 743331', availability: 'Mon-Fri, 8am-6pm' },
                { id: '3', role: 'Wellbeing Team', description: 'For health support and wellness enquiries.', phone: '07957 743331', availability: 'Mon-Fri, 9am-5pm' },
                { id: '4', role: 'Restaurant Manager', description: 'For dining reservations and private events.', phone: '07957 743331', availability: 'Daily, 10am-8pm' },
            ]);
            setOpeningHours([
                { id: '1', venue: 'Restaurant', weekday_hours: '7:30am - 9:00pm', weekend_hours: '8:00am - 9:00pm' },
                { id: '2', venue: 'Bar & Lounge', weekday_hours: '10:00am - 10:00pm', weekend_hours: '10:00am - 11:00pm' },
                { id: '3', venue: 'Wellness Centre', weekday_hours: '6:30am - 9:00pm', weekend_hours: '7:00am - 8:00pm' },
                { id: '4', venue: 'Reception', weekday_hours: '24 hours', weekend_hours: '24 hours' },
                { id: '5', venue: 'Hair Salon', weekday_hours: '9:00am - 5:00pm', weekend_hours: 'By appointment' },
            ]);
        }
    };

    const fetchWelcomeData = async () => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [areasRes, contactsRes, hoursRes] = await Promise.all([
                axios.get(`${API}/welcome/site-areas`, { headers }),
                axios.get(`${API}/welcome/contacts`, { headers }),
                axios.get(`${API}/welcome/hours`, { headers }),
            ]);
            // If no data exists from backend, use defaults
            if (areasRes.data.length === 0 && contactsRes.data.length === 0 && hoursRes.data.length === 0) {
                setDefaults();
            } else {
                if (areasRes.data.length > 0) setSiteAreas(areasRes.data);
                if (contactsRes.data.length > 0) setKeyContacts(contactsRes.data);
                if (hoursRes.data.length > 0) setOpeningHours(hoursRes.data);
                // Still set defaults for empty sections
                if (areasRes.data.length === 0) setDefaults();
            }
        } catch (error) {
            console.error('Error fetching welcome data:', error);
            setDefaults();
        } finally {
            setLoading(false);
        }
    };

    const getIcon = (iconName) => {
        return iconMap[iconName] || Building2;
    };

    const firstWeekSuggestions = [
        { day: 'Day 1', title: 'Settle In', description: 'Unpack, explore your new home, and meet your neighbours.' },
        { day: 'Day 2', title: 'Reception Tour', description: 'Visit reception for your welcome pack and site orientation.' },
        { day: 'Day 3', title: 'Try the Restaurant', description: 'Enjoy breakfast or lunch in our restaurant - on us!' },
        { day: 'Day 4', title: 'Wellness Visit', description: 'Tour the wellness centre and book your first class.' },
        { day: 'Day 5', title: 'Social Event', description: 'Attend a coffee morning or social gathering to meet residents.' },
        { day: 'Day 6', title: 'Explore Grounds', description: 'Take a walk through the gardens and discover the local paths.' },
        { day: 'Day 7', title: 'Book Activities', description: 'Browse upcoming events and book your favourites!' },
    ];

    return (
        <div className="min-h-screen bg-stone-50 pb-8" data-testid="welcome-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-stone-800 to-stone-900 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-stone-300 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                </button>
                <h1 className="text-3xl font-serif mb-2">Welcome Guide</h1>
                <p className="text-stone-300">
                    Everything you need to know about life at {organization?.name || 'your residence'}
                </p>
            </div>

            {/* Section Navigation */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-2 flex gap-1 overflow-x-auto scrollbar-hide">
                    {sections.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
                                activeSection === section.id
                                    ? 'bg-stone-800 text-white'
                                    : 'text-stone-600 hover:bg-stone-100'
                            }`}
                        >
                            <section.icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{section.title}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Sections */}
            <div className="px-4 mt-6">
                {/* Introduction */}
                {activeSection === 'intro' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl p-6 border border-stone-200">
                            <h2 className="text-2xl font-serif text-stone-800 mb-4">
                                Welcome to {organization?.name || 'Your New Home'}
                            </h2>
                            <div className="prose prose-stone">
                                <p className="text-stone-600 leading-relaxed">
                                    We're delighted to welcome you to our community. {organization?.name || 'This residence'} offers 
                                    a lifestyle at one with nature, complemented by exceptional services spanning 
                                    security, dining, and wellness.
                                </p>
                                <p className="text-stone-600 leading-relaxed mt-4">
                                    This guide will help you settle in and discover everything your new home 
                                    has to offer. From our award-winning restaurant to our comprehensive wellness 
                                    facilities, you'll find a wealth of experiences waiting for you.
                                </p>
                            </div>
                        </div>

                        <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                            <h3 className="font-semibold text-amber-800 mb-3">How Everything Works</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-amber-600 mt-0.5" />
                                    <span className="text-amber-900">Use this app to book activities, dining, and services</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-amber-600 mt-0.5" />
                                    <span className="text-amber-900">Your credits can be used for activities and experiences</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-amber-600 mt-0.5" />
                                    <span className="text-amber-900">Reception is available 24/7 for any assistance</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-amber-600 mt-0.5" />
                                    <span className="text-amber-900">Join clubs and groups to meet fellow residents</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

                {/* Site Overview */}
                {activeSection === 'site' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-stone-800 mb-4">Explore the Site</h2>
                        {siteAreas.map((area) => {
                            const IconComponent = getIcon(area.icon);
                            return (
                                <div key={area.id} className="bg-white rounded-2xl p-5 border border-stone-200">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
                                            <IconComponent className="w-6 h-6 text-stone-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-stone-800">{area.title}</h3>
                                            <p className="text-stone-600 text-sm mt-1">{area.description}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Key Contacts */}
                {activeSection === 'contacts' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-stone-800 mb-4">Key Contacts</h2>
                        {keyContacts.map((contact) => (
                            <div key={contact.id} className="bg-white rounded-2xl p-5 border border-stone-200">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h3 className="font-semibold text-stone-800">{contact.role}</h3>
                                        <p className="text-stone-500 text-sm mt-1">{contact.description}</p>
                                        <div className="flex items-center gap-4 mt-3">
                                            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-amber-700">
                                                <Phone className="w-4 h-4" />
                                                <span className="text-sm font-medium">{contact.phone}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-stone-100">
                                    <span className="text-xs text-stone-500">Available: {contact.availability}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Opening Hours */}
                {activeSection === 'hours' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-stone-800 mb-4">Opening Hours</h2>
                        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                            <div className="grid grid-cols-3 bg-stone-100 px-5 py-3 text-sm font-medium text-stone-600">
                                <span>Venue</span>
                                <span className="text-center">Mon-Fri</span>
                                <span className="text-center">Sat-Sun</span>
                            </div>
                            {openingHours.map((item, index) => (
                                <div 
                                    key={item.id} 
                                    className={`grid grid-cols-3 px-5 py-4 text-sm ${
                                        index < openingHours.length - 1 ? 'border-b border-stone-100' : ''
                                    }`}
                                >
                                    <span className="font-medium text-stone-800">{item.venue}</span>
                                    <span className="text-center text-stone-600">{item.weekday_hours}</span>
                                    <span className="text-center text-stone-600">{item.weekend_hours}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* First Week */}
                {activeSection === 'first-week' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-semibold text-stone-800 mb-4">Your First Week</h2>
                        <p className="text-stone-600 mb-6">
                            Here's a suggested guide to help you settle in and make the most of your first week.
                        </p>
                        {firstWeekSuggestions.map((item, index) => (
                            <div key={index} className="bg-white rounded-2xl p-5 border border-stone-200 flex gap-4">
                                <div className="w-16 h-16 rounded-xl bg-amber-100 flex flex-col items-center justify-center flex-shrink-0">
                                    <span className="text-xs text-amber-600 font-medium">{item.day.split(' ')[0]}</span>
                                    <span className="text-2xl font-bold text-amber-700">{item.day.split(' ')[1]}</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-stone-800">{item.title}</h3>
                                    <p className="text-stone-500 text-sm mt-1">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Floating Help Button */}
            <div className="fixed bottom-24 right-4">
                <Button
                    onClick={() => navigate('/help')}
                    className="bg-stone-800 hover:bg-stone-700 text-white rounded-full shadow-lg px-5 h-12"
                >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Need Help?
                </Button>
            </div>
        </div>
    );
}

export default WelcomePage;
